import React, { useEffect, useState } from "react";
import { dms } from "./api";

const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const box = { background: "var(--card, #fff)", border: "1px solid rgba(128,128,128,0.25)", borderRadius: 14, padding: 16 };
const grid2 = { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" };
const cell = { padding: "6px 8px", fontSize: 13, textAlign: "right", whiteSpace: "nowrap" };
const cellL = { ...cell, textAlign: "left", whiteSpace: "normal" };

function Section({ title, hint, children }) {
  return (
    <div style={box}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      {hint ? <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{hint}</div> : null}
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}
function Empty({ text }) { return <div style={{ fontSize: 13, opacity: 0.6 }}>{text}</div>; }
function Stat({ label, value, sub }) {
  return (
    <div style={{ border: "1px solid rgba(128,128,128,0.2)", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, opacity: 0.6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, opacity: 0.6 }}>{sub}</div> : null}
    </div>
  );
}
function Bar({ pct, color }) {
  return (
    <div style={{ height: 8, borderRadius: 999, background: "rgba(128,128,128,0.18)", overflow: "hidden" }}>
      <div style={{ width: Math.min(100, Math.max(0, pct || 0)) + "%", height: "100%", background: color || "#b8860b" }} />
    </div>
  );
}
const pctColor = (p) => (p === null || p === undefined ? "#64748b" : p >= 100 ? "#b91c1c" : p >= 75 ? "#c2410c" : p >= 50 ? "#b45309" : "#15803d");

function Rows({ rows, left, right }) {
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "6px 0", borderTop: i ? "1px solid rgba(128,128,128,0.15)" : "none" }}>
          <span>{left(r)}</span><b style={{ textAlign: "right" }}>{right(r)}</b>
        </div>
      ))}
    </div>
  );
}

export default function OwnerInsightsV2({ period, distId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    setErr(false);
    dms.ownerInsightsV2({ period, distributor_id: distId || undefined })
      .then((r) => { if (live) setD(r); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [period, distId]);
  if (err) return <Empty text="Could not load insights. Please refresh the page." />;
  if (!d) return <Empty text="Loading insights..." />;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Section title="Distributor Scorecard" hint="Sales = retailer bills in this period. Bought = primary orders from you.">
        {d.scorecard && d.scorecard.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ opacity: 0.6, fontSize: 11, textTransform: "uppercase" }}>
                  <th style={cellL}>Distributor</th><th style={cell}>Sales</th><th style={cell}>Bills</th><th style={cell}>Bought</th><th style={cell}>Outstanding</th><th style={cell}>Credit used</th>
                </tr>
              </thead>
              <tbody>
                {d.scorecard.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid rgba(128,128,128,0.15)" }}>
                    <td style={cellL}>{r.name}</td>
                    <td style={cell}>{inr(r.sales)}</td>
                    <td style={cell}>{r.bills}</td>
                    <td style={cell}>{inr(r.bought)}</td>
                    <td style={cell}>{inr(r.outstanding)}</td>
                    <td style={{ ...cell, color: pctColor(r.pct), fontWeight: 600 }}>{r.pct === null || r.pct === undefined ? "No limit" : r.pct + "%"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty text="No distributors found." />}
      </Section>

      <div style={grid2}>
        <Section title="Outstanding Aging" hint="Money retailers still owe, by how old the bill is.">
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>{inr(d.aging.total)}</div>
          {d.aging.buckets.map((b, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span>{b.label} <span style={{ opacity: 0.6 }}>({b.bills} bills)</span></span><b>{inr(b.amount)}</b>
              </div>
              <Bar pct={d.aging.total > 0 ? (b.amount / d.aging.total) * 100 : 0} color={["#15803d", "#b45309", "#c2410c", "#b91c1c"][i]} />
            </div>
          ))}
        </Section>

        <Section title="Stock Health" hint="Distributor stock. Dead stock = in stock, but nothing went out in 60 days.">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <Stat label="Stock value" value={inr(d.stock.value)} sub="Positive stock only" />
            <Stat label="Dead stock" value={inr(d.stock.dead_value)} sub={d.stock.dead_count + " items"} />
          </div>
          {d.stock.negative_rows ? <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 8 }}>{d.stock.negative_rows} items are negative and not counted.</div> : null}
          {d.stock.dead && d.stock.dead.length ? (
            <Rows rows={d.stock.dead} left={(r) => r.distributor + ": " + r.product} right={(r) => r.boxes + " box | " + inr(r.value)} />
          ) : <Empty text="No dead stock." />}
        </Section>
      </div>

      <div style={grid2}>
        <Section title="Top Products" hint="By sales amount in this period.">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <Stat label="Sales" value={inr(d.top_products.amount)} />
            <Stat label="Litres sold" value={Number(d.top_products.litres || 0).toLocaleString("en-IN")} />
          </div>
          {d.top_products.rows.length ? (
            <Rows rows={d.top_products.rows} left={(r) => r.name} right={(r) => inr(r.amount) + " | " + r.litres + " L"} />
          ) : <Empty text="No sales in this period." />}
        </Section>

        <Section title="Retailer Insights" hint="Sleeping = no bill in the last 30 days.">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <Stat label="Active retailers" value={d.retailers.total} />
            <Stat label="Buying now" value={d.retailers.buying} sub="In this period" />
            <Stat label="New retailers" value={d.retailers.new} sub="In this period" />
            <Stat label="Sleeping" value={d.retailers.sleeping} />
          </div>
          {d.retailers.top.length ? (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Top retailers</div>
              <Rows rows={d.retailers.top} left={(r) => r.name} right={(r) => inr(r.amount)} />
            </div>
          ) : null}
          {d.retailers.sleeping_list.length ? (
            <div>
              <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>Sleeping retailers</div>
              <Rows rows={d.retailers.sleeping_list} left={(r) => r.name} right={(r) => (r.days === null ? "Never billed" : r.days + " days")} />
            </div>
          ) : null}
        </Section>
      </div>

      <div style={grid2}>
        <Section title="Field Team" hint="Punch-ins and sales by salesperson.">
          <Stat label="Punched in today" value={d.field.punched_today + " of " + d.field.total} />
          {d.field.not_in.length ? <div style={{ fontSize: 12, margin: "8px 0", color: "#b45309" }}>Not punched in: {d.field.not_in.join(", ")}</div> : null}
          {d.field.rows.length ? (
            <Rows rows={d.field.rows} left={(r) => r.name} right={(r) => r.days + " days | " + r.new_retailers + " new | " + inr(r.sales)} />
          ) : <Empty text="No salespersons assigned yet." />}
        </Section>

        <Section title="Recent Activity" hint="Latest bills, orders and returns.">
          {d.recent.length ? (
            <Rows rows={d.recent} left={(r) => r.kind + ": " + r.text} right={(r) => (r.amount === null ? "" : inr(r.amount))} />
          ) : <Empty text="No activity yet." />}
        </Section>
      </div>
    </div>
  );
}
// END_INSIGHTS_V2
